import {
  Expressions,
  Component,
  matchExpression,
  Types,
  Plugins,
  Type,
  inferType,
  CheckerState,
  ElementPreset,
  ExpressionPath,
  Variable,
  withSystem,
  EvalEnv,
} from '@packages/systems/wfdl';
import {Result, Err, Ok} from '@packages/utilities/fp/result';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  namesEqual,
  qualify,
  typeName,
  identifier,
  getName,
} from '@packages/systems/wfdl-Qualified';
import {
  ECall,
  EElement,
  EList,
  ERecord,
  EText,
  EUnion,
  Expression,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  completeTree,
  getComponentInstRootElement,
  isUserComponentInst,
  updateComponentRenderInComponentMap,
} from '@packages/utilities/platform';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';

import {
  isDynamoWrapper,
  isFormForm,
  isImageElement,
  isSlot,
} from '@packages/utilities/elementTypeUtils';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {weakMemo} from '@packages/utilities/memo';
import {Basic} from '@packages/systems/core/plugins';
import {
  getPluginsFromState,
  selectCurrentComponent,
  selectCurrentComponentType,
  selectElementFromSelectedNodeNativeId,
} from '@packages/utilities/platform-selectors';
import {Pair, getFirst, getSecond, pair} from '@packages/utilities/Pair';
import {
  instantiateExpressionWithUniqueIds,
  instantiateExpressionWithUniqueIdsState,
  UniqueIdsState,
} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {incrementGuid, generateGuid} from '@packages/utilities/guid';
import {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {
  getCurrentVariantStateKeyFromElement,
  getBaseVariantDisplayName,
  getVariantDisplayName,
} from '@packages/systems/component-style-variants';
import {COMBINATORS} from '@packages/systems/core/constants/Selectors';
import {List} from 'immutable';
import {
  accumulateStyleBlocks,
  sortByDepth,
  updateCssPropertyMap,
  buildStyleBlock,
} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
import {
  emptyArray,
  identity,
  concat,
  constant,
} from '@packages/utilities/fp/utils';
import type {StyleBlock} from '@packages/systems/style-legacy/records';
import {StyleBlockId} from '@packages/systems/style/types';
import {
  createVariantKey,
  parseVariantKey,
} from '@packages/systems/style-legacy/variants';
import {
  NO_PSEUDO,
  StyleBlockVariantType,
} from '@packages/systems/core/utils/StyleTypes';
import {BREAKPOINT_ID_MAIN} from '@packages/systems/style/breakpoint-ids';
import {validateUnlink} from './validateUnlink';
import type {ComponentInstanceUnlinkedAction} from './actions';
import {findAllReferencesToField} from '@packages/systems/user-components/unbind-references';
import {setEmptyDynamoEnv} from '@packages/systems/dynamo/graphql';
import {
  removeHolesFromLists,
  replaceApplyPredicateWithHole,
  getMatchingCallToVariable,
  trimGroupsAndConditions,
} from '@packages/systems/plugins-utils/Conditionals';
import {deconstructAppend} from '@packages/utilities/append';
import {
  createOverlayTargetExpression,
  rewriteOverlayTargetReferences,
} from '@packages/systems/overlays';

export type ComponentUnlinkResult = {
  componentMap: Component.ComponentMap;
  styleBlockState: StyleBlockStateType;
};

const conditionalVariable = Variable.qualified(
  qualify(Plugins.name('Conditionals'), identifier('Conditional'))
);

const getPropertyBindingKey = matchExpression({
  Select: ({from, prop}) => {
    if (Expressions.isDataVariable(from)) {
      return prop;
    }
    return null;
  },
  _: () => null,
});

const imageTypeName = qualify(Plugins.name('Asset'), typeName('Image'));

// Remove `attr.src` if an Image element is found with a binding to `img`
const removeImageAttrSrc = (
  expr: Expression,
  componentRootType: Type
): Expression => {
  const type = componentRootType;
  const typeFields = Types.getRecordFields(type);
  // Find all Image typed override fields from root component data type
  const imageFieldIDs = typeFields
    ? Object.keys(typeFields).filter((fieldID) => {
        const fieldDataType = typeFields[fieldID];
        const name = fieldDataType
          ? Types.getTypeConstructor(fieldDataType)
          : null;
        return name && namesEqual(name, imageTypeName);
      })
    : [];

  const imgField = Expressions.getIn(expr, [{in: 'Record', at: 'img'}]);
  if (!imgField) {
    return expr;
  }

  const imgFieldSelect = Expressions.getSelect(imgField);
  if (
    imgFieldSelect &&
    Expressions.isDataVariable(imgFieldSelect.from) &&
    imageFieldIDs.some((id) => imgFieldSelect.prop === id)
  ) {
    const nextElementData = Expressions.deleteInRecord(expr, [
      {in: 'Record', at: 'attr'},
      {in: 'Record', at: 'src'},
    ]);
    return nextElementData;
  }

  return expr;
};

// Ensure only 1 flow exists to a source, during unlink we want
// to reset any Forms that are bound to Flow sources
const removeFromSourceFlowId = (expr: Expression): Expression => {
  const nextElementData = Expressions.sparseSetIn(
    expr,
    [{in: 'Record', at: 'Source'}],
    EUnion({
      tag: 'Default form',
      value: ERecord({}),
    })
  );
  return nextElementData;
};

// Slot elements should not exist outside of components so we remove
// any potential Slot elements that might have been created to Blocks.
// We want to retain the Slot content so we make sure to pass in the old
// children and styleBlockIds to the new element.
// We also want to carry over the tag from the Slot to the new Block element.
const childrenPath: ExpressionPath = [{in: 'Record', at: 'children'}];
const styleBlockIdsPath: ExpressionPath = [{in: 'Record', at: 'styleBlockIds'}];
const tagPath: ExpressionPath = [{in: 'Record', at: 'tag'}];
const convertSlotToBlock = (
  el: ExpressionElement,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ExpressionElement => {
  const blockElement = Expressions.getElement(
    completeTree(
      ElementPreset.instantiateFactory(Basic.elementPresets.DivBlock, el.id)
        .element,
      components,
      plugins
    )
  );

  const tag = Expressions.getIn(Expressions.getElementData(el), tagPath);

  const oldChildren = Expressions.getIn(
    Expressions.getElementData(el),
    childrenPath
  );
  const styleBlockIds = Expressions.getIn(
    Expressions.getElementData(el),
    styleBlockIdsPath
  );

  // If we fail to create a block element or the old children are missing
  // we return the original element since we don't know what to convert the
  // Slot to. This should never happen as long as our elementPresets are correct.
  if (!blockElement || !oldChildren) {
    return el;
  }

  // Set old children to the new element
  let nextElementData = Expressions.sparseSetIn(
    Expressions.getElementData(blockElement),
    childrenPath,
    oldChildren
  );

  // Set slot's tag to the new element
  if (tag) {
    nextElementData = Expressions.sparseSetIn(nextElementData, tagPath, tag);
  }

  // If the original Slot contained styleBlockIds we want to retain them
  if (styleBlockIds) {
    // Set old styleBlockIds to the new element
    nextElementData = Expressions.sparseSetIn(
      nextElementData,
      styleBlockIdsPath,
      styleBlockIds
    );
  }

  return Expressions.setElementData(blockElement, nextElementData);
};

const getDataFieldIds: (arg1: Expression) => string[] = Expressions.everything(
  concat
)(
  matchExpression({
    Select: ({from, prop}) => {
      if (Expressions.isDataVariable(from)) {
        return Expressions.stop([prop]);
      }
      // ignore selects that do not target Data env
      return Expressions.resume(emptyArray);
    },
    _: constant(Expressions.resume(emptyArray)),
  })
);

const getExpressionFromDataField = (
  rootDataType: Type,
  fieldId: string,
  plugins: Plugins.PluginsType
) => {
  const rootDataTypeFields = Types.getRecordFields(rootDataType) || {};
  const unbindDataType = rootDataTypeFields[fieldId];
  if (!unbindDataType) {
    throw new TypeError(
      `Unable to find field from Type: ${Types.print(
        rootDataType
      )}, fieldId: ${fieldId}`
    );
  }

  return getCurrentOrDefaultExpression(plugins)(unbindDataType);
};

const getDefaultExprFromField = (
  rootDataType: Type,
  fieldId: string,
  plugins: Plugins.PluginsType
): Expression => {
  const rootDataTypeFields = Types.getRecordFields(rootDataType) || {};
  const unbindDataType = rootDataTypeFields[fieldId];
  if (!unbindDataType) {
    throw new TypeError(
      `Unable to find field from Type: ${Types.print(
        rootDataType
      )}, fieldId: ${fieldId}`
    );
  }
  return getCurrentOrDefaultExpression(plugins)(unbindDataType);
};

const getEvaluationTargetType = (
  plugins: Plugins.PluginsType,
  implicitFn: Expression | null,
  componentDataType: Type,
  fieldId: string
): Type | null => {
  if (implicitFn) {
    const deconstructedCall = Expressions.deconstructCall(implicitFn);
    const variable = Expressions.getVariable(deconstructedCall.fun);
    if (!variable) {
      return null;
    }

    return getImplicitFnReturnType(plugins, variable);
  } else {
    // Get target type from componentDataType
    const componentDataTypeFields = Types.getRecordFields(componentDataType);
    if (!componentDataTypeFields) {
      return null;
    }
    return componentDataTypeFields[fieldId] || null;
  }
};

const getImplicitFnReturnType = (
  plugins: Plugins.PluginsType,
  implicitFnVar: Variable.VariableType
): Type | null => {
  const functionType = Variable.match({
    qualified: (v) => plugins.values.get(v),
    unqualified: () => undefined,
  })(implicitFnVar);
  if (!functionType || functionType.envType.implicit === 'no') {
    return null;
  }
  const deconstructedFunctionType = Types.deconstructFunctionType(
    functionType.envType.dataType
  );
  return deconstructedFunctionType.ret;
};

const createExprForEvaluation = (
  implicitFn: Expression | null,
  overrideExpr: Expression | undefined,
  componentDataType: Type,
  fieldId: string,
  plugins: Plugins.PluginsType
): Expression => {
  const newExpr =
    overrideExpr ||
    getExpressionFromDataField(componentDataType, fieldId, plugins);
  const uniqueArg = getSecond(
    instantiateExpressionWithUniqueIds(newExpr)(generateGuid())
  );
  if (implicitFn) {
    // implicitFn here is `implicitFn(data.foo)` and our newExpr represents
    // what we want to replace as the argument.
    const deconstructedCall = Expressions.deconstructCall(implicitFn);
    return ECall({fun: deconstructedCall.fun, arg: uniqueArg});
  }
  return uniqueArg;
};
const tryEvaluateExpression = (
  expr: Expression,
  plugins: Plugins.PluginsType,
  targetType: Type,
  dataValues: Record<string, unknown>
): Expression | null => {
  try {
    const baseEnv = setEmptyDynamoEnv(Plugins.getEvalEnv(plugins));
    const envWithData = EvalEnv.insertValue(
      baseEnv,
      Variable.unqualified(identifier('data')),
      dataValues
    );
    const evaluatedValue = Expressions.evaluate(envWithData, expr);
    const newExpr = getCurrentOrDefaultExpression(plugins)(
      withSystem({defaultValue: evaluatedValue}, targetType)
    );
    return getSecond(
      instantiateExpressionWithUniqueIds(newExpr)(generateGuid())
    );
  } catch {
    return null;
  }
};

// Given potentially some `implicitFn(data.foo)` or just `data.foo` without
// an implicit and cases where `data.foo` (overrideExpr) can be some Conditional
// or not exist at all, we want to evaluate the expression and return a static
// value if possible.
//
// This is a simplified version of unbindReferences that is used
// during prop deletion. We can not reuse that util as we expect and handle
// cases where the evaluation fails the evaluation for example when seeing
// Dynamo values. The unbindReferences util would throw in those cases.
const safeReplaceValueWithEvaluationExpr = (
  plugins: Plugins.PluginsType,
  implicitFn: Expression | null,
  overrideExpr: Expression | undefined,
  componentDataType: Type,
  fieldId: string,
  dataValues: Record<string, unknown>
): Expression | null => {
  // This util first evaluates the current expression, and then transforms it
  // back to an expression using the target type of the field.
  const targetType = getEvaluationTargetType(
    plugins,
    implicitFn,
    componentDataType,
    fieldId
  );
  if (!targetType) {
    return null;
  }

  const exprToEvaluate = createExprForEvaluation(
    implicitFn,
    overrideExpr,
    componentDataType,
    fieldId,
    plugins
  );
  const evaluatedValue = tryEvaluateExpression(
    exprToEvaluate,
    plugins,
    targetType,
    dataValues
  );
  if (!evaluatedValue) {
    // If evaluation fails with the provided expressions try to evaluate again
    // using the default expression of the field. This allows us to handle data
    // missing from the env we use for unlinking.
    const defaultExprToEvaluate = createExprForEvaluation(
      implicitFn,
      undefined,
      componentDataType,
      fieldId,
      plugins
    );
    return tryEvaluateExpression(
      defaultExprToEvaluate,
      plugins,
      targetType,
      dataValues
    );
  }
  return evaluatedValue;
};

// A simplified version of safeReplaceValueWithEvaluationExpr that is only
// used when we know we are passing in an implicitFn and an overrideExpr.
// We never use override fields in this version and instead just return
// null when evaluation fails.
const safeReplaceValueWithEvaluationFromImplicit = (
  plugins: Plugins.PluginsType,
  implicitFn: Expression,
  overrideExpr: Expression,
  dataValues: Record<string, unknown>
): Expression | null => {
  const deconstructedCall = Expressions.deconstructCall(implicitFn);
  const variable = Expressions.getVariable(deconstructedCall.fun);
  const targetType = variable
    ? getImplicitFnReturnType(plugins, variable)
    : null;
  if (!targetType) {
    return null;
  }

  const uniqueArg = getSecond(
    instantiateExpressionWithUniqueIds(overrideExpr)(generateGuid())
  );
  const exprToEvaluate = ECall({fun: deconstructedCall.fun, arg: uniqueArg});
  return tryEvaluateExpression(exprToEvaluate, plugins, targetType, dataValues);
};

const condVisPath: ExpressionPath = [
  {in: 'Record', at: 'visibility'},
  {in: 'Record', at: 'conditions'},
];

type TaggedUnlinkedConditional = {
  tag: 'Conditional' | 'Fallback';
  val: Expression;
};

const replacePropsWithinConditional = (
  conditionalArg: Expression,
  conditionalFun: Expression,
  instanceOverrideFields: Record<string, Expression>,
  componentDataType: Type,
  plugins: Plugins.PluginsType,
  dataValues: Record<string, unknown>
): TaggedUnlinkedConditional => {
  let newConditionalArg = conditionalArg;
  // We look for references to props in the argument expression. We handle
  // all of these the same way, except when they are the conditional source
  // of a condition list item.

  // Get all different props used in the arg
  const fieldIds = getDataFieldIds(newConditionalArg);

  // Then for all of these props get a new replacement value based on the instance
  // we are unlinking and all paths where the new value should go.
  const propValueMap: Record<
    string,
    {value: Expression; paths: ExpressionPath[]}
  > = fieldIds.reduce(
    (
      acc: Record<string, {value: Expression; paths: ExpressionPath[]}>,
      curr: string
    ) => {
      let replaceExprCandidate: Expression | null = null;
      const overrideExpr = instanceOverrideFields[curr];
      if (overrideExpr && Expressions.isBinding(overrideExpr)) {
        // If the prop is a Conditional we try to evaluate it
        const propConditionalCall = getMatchingCallToVariable(
          overrideExpr,
          conditionalVariable
        );
        if (propConditionalCall) {
          replaceExprCandidate = safeReplaceValueWithEvaluationExpr(
            plugins,
            null,
            overrideExpr,
            componentDataType,
            curr,
            dataValues
          );
        } else {
          // If the prop is a binding, but not a Conditional we use the binding as is
          replaceExprCandidate = overrideExpr;
        }
      } else if (overrideExpr) {
        // For static values use the overrideExpr as is
        replaceExprCandidate = overrideExpr;
      }
      // If not value found on instance (or evaluation of a Conditional fails),
      // then use the default value from the component
      const replaceExpr =
        replaceExprCandidate ||
        getDefaultExprFromField(componentDataType, curr, plugins);
      const replacePaths = findAllReferencesToField(newConditionalArg, curr);

      acc[curr] = {value: replaceExpr, paths: replacePaths};
      return acc;
    },
    {}
  );

  // Now that we have a propValueMap lets iterate it and replace all values from
  // the arg with the new values.
  Object.entries(propValueMap).forEach(([_, {value, paths}]) => {
    paths.forEach((path) => {
      let valueSet = false;
      // Remove all conditions that point to the condition source
      const replacedValue = replaceApplyPredicateWithHole(
        newConditionalArg,
        path
      );
      if (replacedValue) {
        newConditionalArg = replacedValue;
        valueSet = true;
      }
      if (!valueSet) {
        newConditionalArg = Expressions.setIn(newConditionalArg, path, value);
      }
    });
  });

  // Now the final step for our new arg is to remove all holes from it
  newConditionalArg =
    Expressions.everywhere(removeHolesFromLists)(newConditionalArg);

  return trimGroupsAndConditions(
    ECall({
      fun: conditionalFun,
      arg: newConditionalArg,
    })
  );
};

// Handles the Visibility::booleanToVisibilityConditions() implicit wrapper
// at condVisPath. This wrapper is specific to conditional visibility and
// needs special handling:
// - Prop bindings directly inside the wrapper (booleanToVisibilityConditions(data.foo))
// - DynamoGateway::dynamoBooleanToUnionBoolean -> dynamoBooleanToVisibilityConditions
// - Evaluating fallback values through the wrapper
// - Falling back to True ^ {} when evaluation fails
const handleVisibilityWrapper = (
  elData: Expression,
  instanceOverrideFields: Record<string, Expression>,
  componentDataType: Type,
  plugins: Plugins.PluginsType,
  dataValues: Record<string, unknown>
): Expression => {
  const condVisExpr = Expressions.getIn(elData, condVisPath);
  if (!condVisExpr) {
    return elData;
  }

  // Check if the conditional visibility expression starts with a call to
  // Visibility::booleanToVisibilityConditions()
  const call = getMatchingCallToVariable(
    condVisExpr,
    Variable.qualified(
      qualify(
        Plugins.name('Visibility'),
        identifier('booleanToVisibilityConditions')
      )
    )
  );
  // All bound values to visibility start with this. Props and Conditions. If it
  // does not exist we can keep the element as is.
  if (!call) {
    return elData;
  }
  // Check if the arg of implicit call to visibility expression starts with a call to
  // Conditionals:::Conditional()
  const conditionCall = getMatchingCallToVariable(
    call.arg,
    conditionalVariable
  );

  if (!conditionCall) {
    // If not bound to Conditionals:::Conditional() then we have a Prop. So
    // the field is `booleanToVisibilityConditions(data)`
    if (Expressions.isBinding(call.arg)) {
      const fieldIds = getDataFieldIds(condVisExpr);
      const firstFoundFieldId = fieldIds[0];
      // The expression might in theory contain multiple overrides, this
      // scenario is not yet possible so we will cross that bridge when we get there
      if (firstFoundFieldId) {
        const overrideExpr = instanceOverrideFields[firstFoundFieldId];
        // If overrideExpr is itself a binding and the underlying field is just
        // `booleanToVisibilityConditions(data)`, we can stamp the value in place of
        // `data` regardless of what it is so just return the element as is and
        // let normal unlinking rules take over.
        if (overrideExpr && Expressions.isBinding(overrideExpr)) {
          // Special case: if our override is to DynamoGateway::dynamoBooleanToUnionBoolean,
          // we don't want to end up with
          // `booleanToVisibilityConditions(DynamoGateway::dynamoBooleanToUnionBoolean)`
          // but instead convert that dynamoBooleanToUnionBoolean to dynamoBooleanToVisibility
          const overrideBoolDynamo = getMatchingCallToVariable(
            overrideExpr,
            Variable.qualified(
              qualify(
                Plugins.name('DynamoGateway'),
                identifier('dynamoBooleanToUnionBoolean')
              )
            )
          );
          if (overrideBoolDynamo) {
            const newExpr = ECall({
              fun: Expressions.qualifiedVariable(
                'DynamoGateway',
                'dynamoBooleanToVisibilityConditions'
              ),
              arg: overrideBoolDynamo.arg,
            });
            return Expressions.setIn(elData, condVisPath, newExpr);
          }
          return elData;
        }
        // If overrideExpr is a static value or undefined we want to
        // evaluate `booleanToVisibilityConditions(data)` and stamp in a static
        // value after unlink. We need to evaluate the whole thing since we
        // do not want to end up in a world with `booleanToVisibilityConditions(True ^ {})`.
        const newExpr = safeReplaceValueWithEvaluationExpr(
          plugins,
          condVisExpr,
          overrideExpr,
          componentDataType,
          firstFoundFieldId,
          dataValues
        );
        if (!newExpr) {
          // If the evaluation still fails set conditional visibility to True.
          // Dropping all the conditions that we were not able to evaluate.
          return Expressions.setIn(
            elData,
            condVisPath,
            EUnion({
              tag: 'True',
              value: ERecord({}),
            })
          );
        }
        return Expressions.setIn(elData, condVisPath, newExpr);
      }
      return elData;
    }
    // Static value inside booleanToVisibilityConditions(). This can happen
    // when the generic pass collapsed a Conditional to its fallback value.
    // Evaluate through the wrapper to produce a proper visibility value.
    const evaluated = safeReplaceValueWithEvaluationFromImplicit(
      plugins,
      condVisExpr,
      call.arg,
      dataValues
    );
    if (!evaluated) {
      return Expressions.setIn(
        elData,
        condVisPath,
        EUnion({
          tag: 'True',
          value: ERecord({}),
        })
      );
    }
    return Expressions.setIn(elData, condVisPath, evaluated);
  } else {
    // If we do have a conditionCall, we have a `booleanToVisibilityConditions(Conditional(..))`
    //
    // Phase 1 already processed the Conditional's prop bindings via
    // replacePropsWithinConditional. Re-trim to determine if it survived
    // or collapsed to a fallback value.
    const newCallArg = trimGroupsAndConditions(
      ECall({
        fun: conditionCall.fun,
        arg: conditionCall.arg,
      })
    );

    // If the newCallArg is a Conditional we can just replace the arg of the
    // booleanToVisibilityConditions() call.
    if (newCallArg.tag === 'Conditional') {
      const newFullCondVis = ECall({
        fun: call.fun,
        arg: newCallArg.val,
      });
      return Expressions.setIn(elData, condVisPath, newFullCondVis);
    } else {
      // Else if we removed the Conditional, and ended up with a
      // fallback value we must create a version without the implicit
      // booleanToVisibilityConditions() call. This is because the fallback
      // is a static value and does not need the implicit call.
      const newFullCondVis = safeReplaceValueWithEvaluationFromImplicit(
        plugins,
        condVisExpr,
        newCallArg.val,
        dataValues
      );
      if (!newFullCondVis) {
        // If the evaluation still fails set conditional visibility to True.
        return Expressions.setIn(
          elData,
          condVisPath,
          EUnion({
            tag: 'True',
            value: ERecord({}),
          })
        );
      }
      return Expressions.setIn(elData, condVisPath, newFullCondVis);
    }
  }
};

// Processes all Conditionals::Conditional() calls found in element data,
// using everywhereWithContext to walk into nested structures (records, lists)
// while stopping at child Element boundaries to avoid re-processing children
// that the outer specialElementUpdates walk handles with correct context.
const processConditionalsInElementData = (
  elData: Expression,
  instanceOverrideFields: Record<string, Expression>,
  componentDataType: Type,
  plugins: Plugins.PluginsType,
  dataValues: Record<string, unknown>
): Expression => {
  return Expressions.everywhereWithContext(
    (expr: Expression) =>
      (insideChildElement: boolean): Pair<boolean, Expression> => {
        if (insideChildElement) {
          return pair(true, expr);
        }
        if (Expressions.getElement(expr)) {
          return pair(true, expr);
        }
        const call = getMatchingCallToVariable(expr, conditionalVariable);
        if (!call) {
          return pair(false, expr);
        }
        return pair(
          false,
          replacePropsWithinConditional(
            call.arg,
            call.fun,
            instanceOverrideFields,
            componentDataType,
            plugins,
            dataValues
          ).val
        );
      }
  )(false, elData);
};

// Cases with parts of conditionals bound to props that we need to handle:
// 1. Top level conditional is bound
// 2. Conditional fallback is bound
// 3. Condition group output is bound
// 4. Conditional group item condition value is bound
// 5. Conditional group item condition source is bound
//
// And the bound values can end up being static values, bindings or conditionals
const handleConditionals = (
  plugins: Plugins.PluginsType,
  components: Component.ComponentMap,
  componentDataType: Type,
  el: ExpressionElement,
  instanceElement: ExpressionElement,
  parentComponentDataType: Type | null
): ExpressionElement => {
  const instanceElData = Expressions.getElementData(instanceElement);
  const instanceRecordFields = Expressions.getRecord(instanceElData) || {};
  const parentDefaults: Record<string, Expression> = {};
  const dataValues: Record<string, unknown> = {};
  // Collect all the necessary data from the parent component as the element data might contain overrides
  // that are Conditional() calls which have parent component prop bindings.
  if (parentComponentDataType) {
    const parentFields = Types.getRecordFields(parentComponentDataType);
    if (parentFields) {
      for (const [fieldId, fieldType] of Object.entries(parentFields)) {
        if (fieldType) {
          parentDefaults[fieldId] =
            getCurrentOrDefaultExpression(plugins)(fieldType);
          dataValues[fieldId] = Types.getTypeDefaultValue(plugins)(fieldType);
        }
      }
    }
  }

  const instanceOverrideFields = {...parentDefaults, ...instanceRecordFields};

  if (isUserComponentInst(components, el)) {
    // For user component instances, use Expressions.everywhere to find and
    // process all Conditional() calls. This is safe because user component
    // instance data contains override values, not a full element tree with
    // children that could cause double-processing.
    const elData = Expressions.getElementData(el);
    const newElData = Expressions.everywhere((expr) => {
      const call = getMatchingCallToVariable(expr, conditionalVariable);
      if (!call) {
        return expr;
      }
      return replacePropsWithinConditional(
        call.arg,
        call.fun,
        instanceOverrideFields,
        componentDataType,
        plugins,
        dataValues
      ).val;
    })(elData);
    return Expressions.setElementData(el, newElData);
  }

  const elData = Expressions.getElementData(el);

  // Phase 1: Process all Conditional() calls generically
  const afterGeneric = processConditionalsInElementData(
    elData,
    instanceOverrideFields,
    componentDataType,
    plugins,
    dataValues
  );

  // Phase 2: Visibility-specific wrapper post-processing
  const afterVisibility = handleVisibilityWrapper(
    afterGeneric,
    instanceOverrideFields,
    componentDataType,
    plugins,
    dataValues
  );

  return Expressions.setElementData(el, afterVisibility);
};

const removeVariableModeFieldData = (
  el: ExpressionElement
): ExpressionElement => {
  const record = Expressions.getRecord(Expressions.getElementData(el));
  if (!record || !record.variableModes) return el;

  const {variableModes: _, ...restRecord} = record;
  return Expressions.setElementData(el, ERecord(restRecord));
};

const XATTR_PATH: ExpressionPath = [{in: 'Record', at: 'xattr'}];

// If an element's xattr is bound to a Basic::Attributes prop
// (`List::Append(staticList)(data.<propId>)`), the generic override
// substitution in `applyOverridesFromInstance` only stamps a value at the
// `data.<propId>` Select site, leaving the `Append` call dangling around it.
// Collapse it into a flat static list here, before that generic pass runs,
// using the instance's current override value for the prop (or its default).
const flattenBoundXAttrAttributes = (
  element: ExpressionElement,
  instanceElement: ExpressionElement,
  componentDataType: Type,
  parentComponentDataType: Type | null,
  plugins: Plugins.PluginsType
): ExpressionElement => {
  const elData = Expressions.getElementData(element);
  const xattrExpr = Expressions.getIn(elData, XATTR_PATH);
  if (!xattrExpr) return element;

  const parts = deconstructAppend(xattrExpr);
  if (parts.length !== 2) return element;

  const [staticSegment, boundArg] = parts;
  const select = boundArg ? Expressions.getSelect(boundArg) : null;
  if (!select || !Expressions.isDataVariable(select.from)) return element;

  const propType = Types.getRecordFields(componentDataType)?.[select.prop];
  if (!propType || !Types.isAttributesType(propType)) return element;

  const overrideExpr = Expressions.getRecord(
    Expressions.getElementData(instanceElement)
  )?.[select.prop];

  // The instance's override for this prop can itself be a passthrough
  // binding to a prop on the component this instance is nested inside
  // (e.g. `data.someParentPropId`) rather than a static value. dataValues
  // gives the evaluator something to resolve those references against —
  // mirrors handleConditionals's identical parentComponentDataType walk
  // above, for the same reason.
  const dataValues: Record<string, unknown> = {};
  if (parentComponentDataType) {
    const parentFields = Types.getRecordFields(parentComponentDataType);
    if (parentFields) {
      for (const [fieldId, fieldType] of Object.entries(parentFields)) {
        if (fieldType) {
          dataValues[fieldId] = Types.getTypeDefaultValue(plugins)(fieldType);
        }
      }
    }
  }

  const evaluatedValue = safeReplaceValueWithEvaluationExpr(
    plugins,
    null,
    overrideExpr,
    componentDataType,
    select.prop,
    dataValues
  );

  const staticItems = staticSegment
    ? (Expressions.getList(staticSegment) ?? [])
    : [];
  const evaluatedItems = evaluatedValue
    ? (Expressions.getList(evaluatedValue) ?? [])
    : [];
  const newXattr = EList([...staticItems, ...evaluatedItems]);

  return Expressions.setElementData(
    element,
    Expressions.setIn(elData, XATTR_PATH, newXattr)
  );
};

const specialElementUpdates = (
  rootElement: ExpressionElement,
  instanceElement: ExpressionElement,
  plugins: Plugins.PluginsType,
  components: Component.ComponentMap,
  componentDataType: Type,
  parentComponentDataType: Type | null
) => {
  return Expressions.everywhere((e: Expression) => {
    const el = Expressions.getElement(e);
    if (!el) {
      return e;
    }

    let nextElement = handleConditionals(
      plugins,
      components,
      componentDataType,
      el,
      instanceElement,
      parentComponentDataType
    );

    nextElement = flattenBoundXAttrAttributes(
      nextElement,
      instanceElement,
      componentDataType,
      parentComponentDataType,
      plugins
    );

    nextElement = removeVariableModeFieldData(nextElement);

    const nextElementData = Expressions.getElementData(nextElement);
    if (isImageElement(el)) {
      const imageElementData = removeImageAttrSrc(
        nextElementData,
        componentDataType
      );
      return EElement(Expressions.setElementData(el, imageElementData));
    }

    if (isFormForm(el)) {
      const formElementData = removeFromSourceFlowId(nextElementData);
      return EElement(Expressions.setElementData(el, formElementData));
    }

    if (isSlot(el)) {
      return EElement(convertSlotToBlock(el, components, plugins));
    }

    return EElement(Expressions.setElementData(el, nextElementData));
  })(EElement(rootElement));
};

const applyOverridesFromInstance = (
  instanceElement: ExpressionElement,
  componentRender: Expression,
  componentType: Type,
  plugins: Plugins.PluginsType
): Expression => {
  const instData = Expressions.getElementData(instanceElement);
  const instDataFields = Expressions.getRecord(instData);
  const typeFields = Types.getRecordFields(componentType);
  if (!typeFields) {
    return componentRender;
  }

  const putInData =
    (e: Expression) =>
    (isCurrentElementDynamoWrapper: boolean): Pair<boolean, Expression> => {
      const element = Expressions.getElement(e);
      if (element) {
        return pair(isDynamoWrapper(element), e);
      }
      const prop = getPropertyBindingKey(e);
      if (!prop) {
        return pair(isCurrentElementDynamoWrapper, e);
      }

      const propValue = instDataFields ? instDataFields[prop] : null;
      // Apply instance override if it exists
      if (propValue) {
        const unWrappedExpr = isCurrentElementDynamoWrapper
          ? matchExpression({
              Distinct: ({value}) => value,
              _: identity,
            })(propValue)
          : propValue;
        return pair(isCurrentElementDynamoWrapper, unWrappedExpr);
      }

      // Apply the default from the root component type
      const propType = typeFields[prop];
      if (propType) {
        const expr = getCurrentOrDefaultExpression(plugins)(propType);
        const unWrappedExpr = isCurrentElementDynamoWrapper
          ? matchExpression({
              Distinct: ({value}) => value,
              _: identity,
            })(expr)
          : expr;
        return pair(isCurrentElementDynamoWrapper, unWrappedExpr);
      }

      // Unkown property, return the original expression
      return pair(isCurrentElementDynamoWrapper, e);
    };
  return Expressions.everywhereWithContext(putInData)(false, componentRender);
};

export const getUnlinkedElement = (
  instanceEl: ExpressionElement,
  rootEl: ExpressionElement,
  componentDataType: Type,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  parentComponentDataType: Type | null
): ExpressionElement | null => {
  // Special logic to remove Image attr.src data if the img field
  // is bound and resetting Form sources to default.
  const nextRootExpr = specialElementUpdates(
    rootEl,
    instanceEl,
    plugins,
    componentMap,
    componentDataType,
    parentComponentDataType
  );

  const overridesAppliedExpr = applyOverridesFromInstance(
    instanceEl,
    nextRootExpr,
    componentDataType,
    plugins
  );

  return Expressions.getElement(overridesAppliedExpr);
};

/**
 * Unlinks a component instance from the component map,
 * replacing it with the root component definition.
 *
 * Order of operations:
 * - Instance is replaced with the root component definition
 * - Special edge case logic is applied to Image, Form and Slot elements
 *   - Image elements have their `attr.src` removed if bound to an image field
 *   - Form elements have their source flow ID reset to default
 *   - Slot elements are converted to block elements
 * - Values bound to Component properties are replaced with either:
 *   - The instance override value if it exists
 *   - The default value from the root component definition
 * - The new child elements get fresh IDs
 * - Child elements whose id has a corresponding id entry in seedUniqueIdsState.idMap will get assigned the id from the idMap.
     This is used to, for example, ensure id consistency across locales.
 * - Popover/Modal target references owned by the unlinked component are
 *   re-owned to `parentComponentType` and remapped to the new element ids;
 *   references to the component root resolve to the retained instance id.
 * - Returns the new root element with the instance ID
 * - Also returns the `UniqueIdsState` after updating IDs to be reused in secondary locales.
 */
export const unlinkComponent = (
  componentMap: Component.ComponentMap,
  instanceElement: ExpressionElement,
  plugins: Plugins.PluginsType,
  seedUniqueIdsState: UniqueIdsState,
  styleBlockState: StyleBlockStateType,
  parentComponentType: Plugins.QualifiedElement
): [ExpressionElement, UniqueIdsState | null, StyleBlockStateType] => {
  // The element is not a user component instance, no need to expand
  if (!isUserComponentInst(componentMap, instanceElement)) {
    return [instanceElement, null, styleBlockState];
  }
  const rootComponent = componentMap.get(instanceElement.type);
  // The component is not found in the map, return the original expression
  // since we are unable to expand the component
  if (!rootComponent) {
    return [instanceElement, null, styleBlockState];
  }

  const rootElement =
    getComponentInstRootElement(componentMap)(instanceElement);
  if (!rootElement) {
    return [instanceElement, null, styleBlockState];
  }

  // Expand the component from the instance element and apply property overrides
  const unlinkedElement = getUnlinkedElement(
    instanceElement,
    rootElement,
    rootComponent.dataType,
    componentMap,
    plugins,
    componentMap.get(parentComponentType)?.dataType ?? null
  );

  if (!unlinkedElement) {
    // we could not expand instanceElement, so no need to refresh ids
    return [instanceElement, null, styleBlockState];
  }

  // Make sure to create unique IDs for the new elements
  // The idMap in seedUniqueIdsState ensures that elements are mapped to the same ID across multiple locales.
  const exprWithFreshIds = instantiateExpressionWithUniqueIdsState(
    Expressions.getElementData(unlinkedElement)
  )({
    id: incrementGuid(seedUniqueIdsState.id),
    idMap: seedUniqueIdsState.idMap,
  });

  const uniqueIdsState = getFirst(exprWithFreshIds);
  // The root keeps the instance id; only descendants receive generated ids.
  const overlayIdMap = {
    ...uniqueIdsState.idMap,
    [rootElement.id]: instanceElement.id,
  };
  const oldComponentKey = getName(instanceElement.type);
  const freshExprData = rewriteOverlayTargetReferences(
    getSecond(exprWithFreshIds),
    (reference, expression) => {
      if (reference.componentKey !== oldComponentKey) return expression;
      return createOverlayTargetExpression({
        ...reference,
        componentKey: getName(parentComponentType),
        targetId: overlayIdMap[reference.targetId] ?? reference.targetId,
      });
    }
  );

  const newElement = {
    ...unlinkedElement,
    id: instanceElement.id,
    type: unlinkedElement.type,
    data: freshExprData,
  };

  const elementWithNewIds = EElement(newElement);

  const {expression: updatedExpression, styleBlockState: nextStyleBlockState} =
    unlinkComponentStyleVariants({
      plugins,
      componentMap,
      instanceElement,
      styleBlockState,
      expression: elementWithNewIds,
    });

  const updatedElement =
    Expressions.getElement(updatedExpression) ?? newElement;
  return [updatedElement, uniqueIdsState, nextStyleBlockState];
};

const containsBinding = Expressions.some(Expressions.isBinding);

export const elementHasBinding = (element: ExpressionElement): boolean =>
  containsBinding(Expressions.getElementData(element));

/**
 * For a given element run an `updateFn` which provides an Annotated Expression
 * at each expression in the element.
 *  - Run the Type Checker for the Element to get the current type for each piece
 *    of the element.
 *  - Run updateFn on each expression in the element.
 *  - Get back the non-contextual expression version and return the original element
 *    with new data.
 */
export const updateElementWithTypeChecking = (
  componentDataType: Type,
  element: ExpressionElement,
  updateFn: (
    e: ContextualExpression,
    plugins: Plugins.PluginsType,
    componentPropType: Type,
    existingState: CheckerState
  ) => ContextualExpression,
  checkerState: CheckerState,
  plugins: Plugins.PluginsType
): ExpressionElement | null => {
  const [, contextualExpression] = inferType(EElement(element))(checkerState);

  const updater = (e: ContextualExpression): ContextualExpression => {
    return updateFn(e, plugins, componentDataType, checkerState);
  };

  const nextContextualExpression =
    ContextualExpressions.everywhere(updater)(contextualExpression);

  const strippedNextExpr = stripAll(nextContextualExpression);
  return Expressions.getElement(strippedNextExpr);
};

type ElementIdToStyleBlockIdsMap = Record<string, StyleBlockId[]>;

const getElementIdToStyleBlockIdsMap = matchExpression({
  Element:
    (element: ExpressionElement, expr: Expression) =>
    (idMap: ElementIdToStyleBlockIdsMap) => {
      const styleBlockIds = getStyleBlockIds(element);
      if (styleBlockIds.length > 0) {
        idMap[element.id] = styleBlockIds;
      }

      return pair(idMap, expr);
    },
  _: (expr: Expression) => (idMap: ElementIdToStyleBlockIdsMap) =>
    pair(idMap, expr),
});

/**
 * Get the element id to style block ids map for a given element.
 */
const getElementIdToStyleBlockIdsMapForElement = (
  el: ExpressionElement
): ElementIdToStyleBlockIdsMap => {
  const mut_idMap: ElementIdToStyleBlockIdsMap = {};
  Expressions.everywhereWithContext(getElementIdToStyleBlockIdsMap)(
    mut_idMap,
    EElement(el)
  );
  return mut_idMap;
};

type StyleBlockIdMap = Record<string, string>;

const DATA_STYLEBLOCKIDS_IN_ELEMENT: ExpressionPath = [
  {in: 'Element'},
  {in: 'Record', at: 'styleBlockIds'},
];

type LastIdToNewIdMap = Record<string, string>;
type ParentIdMap = Record<string, string>;
type IntermediateIdMap = Record<string, string>;

/**
 * Handle unlinking a component instance and its style variants.
 *
 * On unlink:
 * - Check if the component instance has a style variant selected
 * - If it does, create a new style block with the variant name
 *   as a combo class to ensure visual consistency on unlink
 * - Updates style block IDs in the expression tree
 * - Returns the updated expression and style block state
 */
export const unlinkComponentStyleVariants = ({
  plugins,
  componentMap,
  instanceElement,
  styleBlockState,
  expression,
}: {
  plugins: Plugins.PluginsType;
  componentMap: Component.ComponentMap;
  instanceElement: ExpressionElement;
  styleBlockState: StyleBlockStateType;
  expression: Expression;
}): {
  expression: Expression;
  styleBlockState: StyleBlockStateType;
} => {
  if (!isUserComponentInst(componentMap, instanceElement))
    return {expression, styleBlockState};

  // only need to unlink style variants if there is a selected variant
  const currentVariantStateKey = getCurrentVariantStateKeyFromElement(
    instanceElement,
    componentMap,
    plugins
  );
  if (!currentVariantStateKey) return {expression, styleBlockState};

  const rootComponent = componentMap.get(instanceElement.type);
  if (!rootComponent) return {expression, styleBlockState};

  const rootElement =
    getComponentInstRootElement(componentMap)(instanceElement);
  if (!rootElement) return {expression, styleBlockState};

  const variantDisplayName = getVariantDisplayName(
    rootComponent,
    currentVariantStateKey
  );
  const styleBlockName =
    variantDisplayName ?? getBaseVariantDisplayName(rootComponent);
  // Maps for tracking style block ID changes
  const idMap: LastIdToNewIdMap = {};
  const parentIdMap: ParentIdMap = {};

  const newStyleBlocks: StyleBlock[] = [];

  const elementIdToStyleBlockIdsMap =
    getElementIdToStyleBlockIdsMapForElement(rootElement);

  const styleBlocks = styleBlockState.styleBlocks;

  for (const styleBlockIds of Object.values(elementIdToStyleBlockIdsMap)) {
    if (styleBlockIds.length === 0) continue;
    const lastId = styleBlockIds[styleBlockIds.length - 1];
    if (!lastId) continue;

    // Generate a new id for the style block
    const newId = generateGuid();

    // Map the lastId to the newId
    idMap[lastId] = newId;

    // Map the newId to the lastId,
    // We use this to keep track of the
    // parent of the new combo class style block
    parentIdMap[newId] = lastId;

    const styleMap: {[key: string]: {[property: string]: string}} = {};

    let lastStyleBlock: StyleBlock | undefined;

    // It is possible for style variants to be applied, then a combo class to be added
    // then a style variant to be applied again. To account for this we need to loop
    // through all the style blocks and collect all the relevant style variant styles.
    for (const styleBlockId of styleBlockIds) {
      const styleBlock = styleBlocks.get(styleBlockId);
      if (!styleBlock) continue;

      if (styleBlockId === lastId) lastStyleBlock = styleBlock;
      const styleBlockVariants = styleBlock.get('variants');

      for (const [variantKey, variant] of styleBlockVariants.entries()) {
        const {
          variantState: variantStateKey,
          pseudoState,
          mediaQuery,
        } = parseVariantKey(variantKey);

        // Only collect styles for the active variant
        if (variantStateKey !== currentVariantStateKey) continue;

        const variantStyle = variant.get('styleLess');
        if (!variantStyle) continue;

        const pseudoStateKey = pseudoState ?? NO_PSEUDO;

        const styleMapKey = createVariantKey({
          mediaQuery,
          pseudoState: pseudoStateKey,
        });

        // Ensure there's an object for properties for this key
        if (!styleMap[styleMapKey]) {
          styleMap[styleMapKey] = {};
        }

        // We _could_ just collect all CSS declarations, which could result in redundant styles.
        // While technically valid (as CSS applies the last declaration for each property),
        // this approach would lead to unnecessarily large style blocks.
        // This implementation keeps only the most recent declaration for each CSS property.
        for (const declaration of variantStyle.split(';')) {
          const [property, value] = declaration
            .split(':')
            .map((s: string) => s.trim());

          if (!property || !value) continue;

          styleMap[styleMapKey]![property] = value;
        }
      }
    }

    if (!lastStyleBlock) continue;

    // Reconstruct the styleString from styleMap, using main and NO_PSEUDO styles
    const mainStyleMapKey = createVariantKey({
      mediaQuery: BREAKPOINT_ID_MAIN,
      pseudoState: NO_PSEUDO,
    });
    const mainStyles = styleMap[mainStyleMapKey] || {};

    const styleString = Object.entries(mainStyles)
      .map(([prop, val]) => `${prop}: ${val};`)
      .join(' ');

    const variants = Object.entries(styleMap).reduce(
      (acc, [key, styles]) => {
        const {mediaQuery, pseudoState} = parseVariantKey(key);
        const variantKey = createVariantKey({mediaQuery, pseudoState});

        if (variantKey === mainStyleMapKey) return acc;

        const variantStyleString = Object.entries(styles)
          .map(([prop, val]) => `${prop}: ${val};`)
          .join(' ');

        acc[variantKey] = {
          styleLess: variantStyleString,
        };
        return acc;
      },
      {} as Record<string, StyleBlockVariantType>
    );

    const newStyleBlock = buildStyleBlock({
      ...lastStyleBlock.toJS(),
      variants,
      styleLess: styleString,
      _id: newId,
      name: styleBlockName,
      comb: COMBINATORS.COMBO,
    });

    newStyleBlocks.push(newStyleBlock);
  }

  // We need to reduce through existing style blocks
  // to account for combo class collision and prevent style overwriting
  const {idMap: intermediateIdMap, state: nextStyleBlockState} = List(
    newStyleBlocks
  )
    .update(sortByDepth)
    .map((styleBlock: StyleBlock): [string[], StyleBlock] => [[], styleBlock])
    .reduce(accumulateStyleBlocks, {
      importStrategy: 'COPY',
      state: styleBlockState,
      renames: emptyArray,
      droppedFonts: emptyArray,
      nextId: generateGuid(),
      idMap: {},
      parentIdMap,
      slugMap: Plugins.getPluginsSlugMap(plugins),
      enforceUniqueComboClasses: true,
      originPlugin: undefined,
    });

  // the initial id map was a map of the lastId: intermediateId
  // the intermediate id map is a map of the intermediateId: newId
  // we need to merge the two maps to get the final id map lastId: newId
  const mergedMap = Object.entries(idMap).reduce(
    (acc, [oldId, intermediateId]) => {
      const newId = (intermediateIdMap as IntermediateIdMap)[intermediateId];
      if (newId !== undefined) {
        acc[oldId] = newId;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  const updatedExpression = addStyleBlockIds(mergedMap)(expression);

  return {
    expression: updatedExpression,
    styleBlockState: updateCssPropertyMap({
      state: nextStyleBlockState,
      idMap: mergedMap,
    }).state,
  };
};

/**
 * Given a style block id map, update the style block ids in the expression.
 * For example, if an element has style block ids [a, b, c] and the map is
 * {a: 'd', b: 'e', c: 'f'} then the expression will be updated to have style
 * block ids [a, b, c, f]. because the last style block id 'c' maps to 'f'.
 * Combo classes are always appended at the end of the style block ids.
 */
const addStyleBlockIds =
  (styleBlockIdMap: StyleBlockIdMap) =>
  (tree: Expression): Expression =>
    Expressions.everywhere((expr: Expression) => {
      const element = Expressions.getElement(expr);
      if (!element) return expr;

      // For each element in the tree, look for styleBlockIds in its data
      return Expressions.updateIn(
        expr,
        DATA_STYLEBLOCKIDS_IN_ELEMENT,
        (exprToUpdate) => {
          const list = Expressions.getList(exprToUpdate);
          if (!list) return exprToUpdate;

          // For each style block ID in the list:
          // 1. Keep the original ID
          // 2. If there's a mapping to a new ID in mergedMap, append it after the original
          // This maintains the original ID while adding any new combo class IDs
          const updatedList = list.flatMap((listExpr) => {
            const id = Expressions.getText(listExpr);
            if (!id) return [listExpr];

            const styleBlockId = styleBlockIdMap[id];
            if (!styleBlockId) return [listExpr];

            return [listExpr, EText(styleBlockId)];
          });

          return EList(updatedList);
        }
      );
    })(tree);

export const handleComponentInstanceUnlinked = weakMemo(
  (
    action: ComponentInstanceUnlinkedAction
  ): Result<string, ComponentUnlinkResult> => {
    const componentMap = action.state.DesignerStore.components;
    const styleBlockState = action.state.StyleBlockStore;

    if (action.payload.skipLocalUnlinkOnCompletion) {
      return Ok({componentMap, styleBlockState});
    }

    // Important to use selectElementFromSelectedNodeNativeId here in order to
    // get the user component instance instead of any element inside it
    const selectedElement = selectElementFromSelectedNodeNativeId(action.state);

    if (!selectedElement) {
      return Ok({componentMap, styleBlockState});
    }
    const currentComponent = selectCurrentComponent(action.state);
    const error =
      validateUnlink(currentComponent)(componentMap)(selectedElement);

    if (error) {
      return Err(error);
    }

    const plugins = getPluginsFromState(action.state);
    const currentComponentType = selectCurrentComponentType(action.state);
    const currentRender = Component.getRender(currentComponent);
    const instanceElement = Expressions.getElementById(
      currentRender,
      selectedElement.id
    );

    if (!instanceElement) {
      return Ok({componentMap, styleBlockState});
    }

    const [unlinkedElement, _, nextStyleBlockState] = unlinkComponent(
      componentMap,
      instanceElement,
      plugins,
      {id: action.payload.initialId, idMap: {}},
      styleBlockState,
      currentComponentType
    );

    /**
     * Add/replace meta attributes with passed-in metadata after unlinking,
     * if provided.
     *
     * @see https://github.com/webflow/webflow/pull/75289
     */
    if (
      action.payload.unlinkedMeta &&
      Object.keys(action.payload.unlinkedMeta).length > 0
    ) {
      unlinkedElement.meta = {
        ...(unlinkedElement.meta ?? {}),
        ...action.payload.unlinkedMeta,
      };
    }

    const nextRender = Expressions.updateElement(
      selectedElement.id,
      () => unlinkedElement,
      currentRender
    );

    return Ok({
      componentMap: updateComponentRenderInComponentMap(
        componentMap,
        currentComponentType,
        nextRender
      ),
      styleBlockState: nextStyleBlockState,
    });
  }
);
